<template>
  <div :class="$style.wrapper">
    <div v-if="!isDetailReady" :class="$style.wrapper__loading">
      <LoadingIndicator />
    </div>
    <section
      v-else-if="
        currentUser !== undefined && currentUserDiagnosisSummary !== undefined
      "
      :class="[$style.wrapper__karte, $style.karte]"
    >
      <div :class="[$style.karte__userProfile, $style.userProfile]">
        <header>
          <div :class="$style.title">
            <p :class="$style.title__text">ユーザー詳細</p>
            <ButtonCircleClose :class="$style.title__close" @click="close" />
          </div>
          <div :class="[$style.userProfile__header, $style.header]">
            <KartePlannerCommentStatusView
              :class="$style.header__status"
              :hasPlannerCommented="!!savedPlannerComment"
            />
            <KartePlannerCommentText
              :class="$style.header__comment"
              :sameBranchPlannerCommented="
                currentUser.karte.sameBranchPlannerCommented
              "
              :plannerCommentCount="plannerCommentCount || 0"
              :userNumberOfMatch="userNumberOfMatch"
            />
            <p :class="$style.header__time">
              <time
                v-if="modifiedAtAttribute"
                :datetime="modifiedAtAttribute"
                >{{ modifiedAtText }}</time
              >
            </p>
          </div>
        </header>
        <UserProfileBox
          v-if="brief"
          :class="[$style.userProfile__box, $style.box]"
          :personalityType="currentUserDiagnosisSummary.result.personality.type"
          :name="currentUser.nickname"
          :brief="brief"
          size="middle"
        >
          <template #characterChip v-if="showChip">
            <ChannelChip v-if="userChannel" :channel="userChannel" />
          </template>
        </UserProfileBox>
      </div>

      <div :class="[$style.karte__diagnosis, $style.diagnosis]">
        <UserProfileInputs
          v-if="karte?.diagnosis"
          :class="$style.diagnosis__basic"
          :diagnosis="karte.diagnosis"
          :show-heading="true"
        />
        <div :class="[$style.diagnosis__additional, $style.additional]">
          <KarteUserConcern v-if="karte" :karte="karte" />
        </div>
      </div>

      <div v-if="!savedPlannerComment">
        <h3>このユーザーの診断結果</h3>
        <button
          :class="$style.showDiagnosis"
          @click.prevent="openDiagnosisModal"
        >
          診断結果を見る
        </button>
      </div>

      <div :class="[$style.karte__comment, $style.comment]">
        <FadeTransition>
          <KartePlannerCommentView
            v-if="savedPlannerComment"
            :plannerComment="savedPlannerComment"
            :plannerSelections="savedPlannerCommentSelections || []"
            :plannerCommentCount="plannerCommentCount || 0"
            :sameBranchPlannerCommented="
              currentUser.karte.sameBranchPlannerCommented
            "
            :class="$style.comment__detail"
          />
          <template v-else>
            <div v-if="loading" :class="$style.comment__loading">
              <LoadingIndicator />
            </div>
            <KartePlannerCommentForm
              v-else-if="commentFormAvailability && viewer"
              v-model:comment="karteCommentFormText"
              v-model:selections="karteCommentFormSelections"
              :class="$style.comment__detail"
              :commentFormAvailability="commentFormAvailability"
              :viewer="viewer"
              @postComment="onRequestPostComment"
            />
          </template>
        </FadeTransition>
      </div>

      <div v-if="savedPlannerComment">
        <h2>このユーザーの診断結果</h2>
        <button
          :class="$style.showDiagnosis"
          @click.prevent="openDiagnosisModal"
        >
          診断結果を見る
        </button>
      </div>

      <component :is="DiagnosisModal">
        <DiagnosisResultModal
          :karte-id="karteIdStr"
          :diagnosis-summary="currentUserDiagnosisSummary"
          :class="$style.karte__modal2"
          @close="closeDiagnosisModal"
        />
      </component>

      <component :is="CommentModal">
        <KartePlannerCommentConfirmationView
          :class="$style.karte__modal"
          :plannerComment="karteCommentFormText"
          :plannerSelections="karteCommentFormSelections"
          :isPostButtonDisabled="isLoadingPostComment"
          :karteUser="currentUser"
          @post="handlePostComment"
          @edit="closeCommentModal"
        />
      </component>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, watch, toRefs, ref } from 'vue'
import { useRouter } from 'vue-router'
import { captureException as newrelicCaptureException } from '@/newrelic'

import { formatISO } from 'date-fns'
import { formatDateTime } from '@/libs/utilities/formatDate'

import ButtonCircleClose from '@/components/atoms/ButtonCircleClose.vue'
import ChannelChip from '@/components/atoms/ChannelChip.vue'
import UserProfileBox from '@/components/molecules/UserProfileBox.vue'
import UserProfileInputs from '@/components/organisms/UserProfile/UserProfileInputs.vue'
import KarteUserConcern from '@/components/organisms/KarteUserConcern/KarteUserConcern.vue'
import KartePlannerCommentForm from '@/pages/KarteListPage/KarteUserDetail/KartePlannerCommentForm.vue'
import KartePlannerCommentView from '@/pages/KarteListPage/KarteUserDetail/KartePlannerCommentView.vue'
import KartePlannerCommentConfirmationView from '@/pages/KarteListPage/KarteUserDetail/KartePlannerCommentConfirmationView.vue'

import KartePlannerCommentStatusView from '../KartePlannerCommentStatusView.vue'
import KartePlannerCommentText from '../KartePlannerCommentText.vue'

import { useAlert } from '@/libs/alertPortal'
import {
  useFetchKarteUserDetailViewQuery,
  usePostKartePlannerCommentMutation,
} from './KarteUserDetailView.generated'
import { useKarteCommentForm } from '../hooks/useKarteCommentForm'
import { useKarteUserDetail } from '../hooks/useKarteUserDetail'

import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'
import FadeTransition from '@/components/transitions/FadeTransition.vue'

import { isPartnerChannel } from '@/models/channel/channel'
import { useModal } from '@/hooks/useModal'
import { KartePlannerCommentUnavailableReason } from '@/types/schema.generated'
import { GenderToString } from '@/models/gender/GenderToString'
import { prefectureLabel } from '@/features/prefecture/prefecture'
import { useDocumentVisibility } from '@vueuse/core'
import DiagnosisResultModal from '@/components/organisms/UserProfile/DiagnosisResultModal.vue'
import { useLogger } from '@/logger/logger'

const props = defineProps<{
  karteId: number
}>()

const emit = defineEmits({
  close: null,
})

const { CommentModal, openCommentModal, closeCommentModal } =
  useModal('comment')
const { DiagnosisModal, openDiagnosisModal, closeDiagnosisModal } =
  useModal('diagnosis')

const alert = useAlert()
const router = useRouter()
const logger = useLogger()

const { karteId: karteIdRef } = toRefs(props)
const karteIdStr = computed(() => karteIdRef.value.toString())

const visibility = useDocumentVisibility()
const isEnabledFetch = ref(visibility.value === 'visible')

// NOTE: 機能として「カルテ詳細ページ -> カルテ詳細ページ」の遷移可能になった際は、useFetchKarteUserDetailViewQueryが返す`refetch`を使うようにする必要あり
const { result, loading, onError, refetch } = useFetchKarteUserDetailViewQuery(
  {
    id: karteIdStr.value,
  },
  () => ({ enabled: isEnabledFetch.value })
)

onError((error): void => {
  if (
    error.graphQLErrors.some(
      gqlError =>
        gqlError.extensions &&
        gqlError.extensions.code === 'PERMISSION_DENIED' &&
        gqlError.path?.[0] === 'karte'
    )
  ) {
    alert.error('ユーザーを閲覧することができません')
    router.replace({ name: 'karteList' })
    return
  }
  alert.error(
    '読込中にエラーが発生しました。お手数ですが再度読み込みをお願いします。'
  )

  newrelicCaptureException(error)
})

const unwatchVisibility = watch(visibility, documentVisibility => {
  if (documentVisibility === 'visible') {
    unwatchVisibility()

    isEnabledFetch.value = true
  }
})

const commentFormAvailability = computed(
  () => result.value?.karte?.kartePlannerCommentAvailability ?? null
)
const viewer = computed(() => result.value?.viewer ?? null)
const savedPlannerComment = computed(
  () => result.value?.karte?.ownKartePlannerComment?.comment ?? null
)
const savedPlannerCommentSelections = computed(
  () => result.value?.karte?.ownKartePlannerComment?.selections ?? null
)

const userNumberOfMatch = computed(
  () => result.value?.karte?.user.numberOfMatch ?? NaN
)

const {
  currentUser,
  currentUserDiagnosisSummary,
  karte,
  fetchUserDetail,
  isDetailReady,
} = useKarteUserDetail(async (error: Error): Promise<void> => {
  alert.error(
    '読込中にエラーが発生しました。お手数ですが再度読み込みをしてください。'
  )

  newrelicCaptureException(error)
  // eslint-disable-next-line fp/no-mutating-methods
  await router.push({
    name: 'karteList',
  })
})

watch(
  karteIdRef,
  async newValue => {
    if (newValue === undefined) {
      return
    }
    await fetchUserDetail(newValue)
  },
  { immediate: true }
)

const close = () => emit('close')

const modifiedAtText = computed(
  () =>
    currentUser.value !== undefined &&
    formatDateTime(currentUser.value.karte.modifiedAt)
)

const modifiedAtAttribute = computed(
  () =>
    currentUser.value !== undefined &&
    formatISO(currentUser.value.karte.modifiedAt)
)

const plannerCommentCount = computed(() => {
  return currentUser.value?.karte.plannerCommentCount
})

const brief = computed(() => {
  if (!result.value) {
    return undefined
  }

  const { user } = result.value.karte

  const age = user.birthYear
    ? `${new Date().getFullYear() - user.birthYear}歳`
    : undefined
  const genderLabel = user.gender ? GenderToString[user.gender] : undefined
  const address = (() => {
    if (user.address) {
      return user.address.prefecture + ' ' + user.address.city
    }
    return user.livingPrefecture
      ? prefectureLabel(user.livingPrefecture)
      : undefined
  })()
  return [address, age, genderLabel].filter(value => value != null).join('・')
})

const showChip = computed(() => {
  if (currentUser.value?.karte.channel === undefined) {
    return false
  }
  return isPartnerChannel(currentUser.value.karte.channel)
})

const userChannel = computed(() => currentUser.value?.karte.channel)

const { karteCommentFormText, karteCommentFormSelections } =
  useKarteCommentForm(karteIdRef)

const onRequestPostComment = () => {
  if (karteCommentFormText.value === '') {
    alert.warning('アプローチ本文が空です。アプローチ内容を入力してください。')
    return
  }

  // 全部空の場合
  // 例： ['', '']（初期値） ['', '', ..... ,'']
  if (karteCommentFormSelections.value.every(selection => selection === '')) {
    alert.warning(
      'アプローチコメントに選択肢は必須です。選択肢を記入して再度お試しください。'
    )
    return
  }

  // 一部空の選択肢がある場合
  // 例：['hoge', ''] ['', 'hoge', ...., '']
  if (karteCommentFormSelections.value.some(selection => selection === '')) {
    alert.warning(
      '空の選択肢は送信できません。選択肢を複数ご記入の上、空の選択肢を削除して再度お試しください。'
    )
    return
  }

  openCommentModal()
}

const onPlannerCommentPostError = (errorMessage: string) => {
  alert.error(errorMessage)
}

const { mutate: postKartePlannerComment, error: postKartePlannerCommentError } =
  usePostKartePlannerCommentMutation({
    update() {
      refetch()
    },
  })

const isLoadingPostComment = ref(false)
const handlePostComment = async () => {
  if (
    currentUser.value?.karte.id === undefined ||
    karteCommentFormText.value === '' ||
    isLoadingPostComment.value
  )
    return

  try {
    isLoadingPostComment.value = true

    // インシデント調査用のログ
    // https://www.notion.so/400f/ltl-people-3755f11dc9a74a58a516d089bf607f62
    // リクエストされる前に、await してログが送信されることを保証する
    await logger.ltlPeople.event.preSendKartePlannerComment(
      logger.buildMeta(),
      {
        data: {
          karteId: currentUser.value.karte.id,
          comment: karteCommentFormText.value,
          selections: karteCommentFormSelections.value,
          referrerType: '',
        },
      }
    )

    const res = await postKartePlannerComment({
      karteID: currentUser.value.karte.id.toString(),
      comment: karteCommentFormText.value,
      selections: karteCommentFormSelections.value,
    })

    if (postKartePlannerCommentError.value !== null || !res || !res.data) {
      onPlannerCommentPostError(
        'アプローチの送信中にエラーが発生しました。お手数ですが、時間を置いて再度送信してください。'
      )
      return
    }

    const { data } = res

    if (
      data.postKartePlannerComment.__typename ===
      'PostKartePlannerCommentMutationErrorPayload'
    ) {
      switch (data.postKartePlannerComment.error) {
        case KartePlannerCommentUnavailableReason.AlreadyCommented:
          onPlannerCommentPostError(
            'すでにこちらのユーザーにコメントを送信済みです。'
          )
          return
        case KartePlannerCommentUnavailableReason.ChannelDoesNotMatch:
          onPlannerCommentPostError(
            '対応するチャネルを持っていないためアプローチできません。'
          )
          return
        case KartePlannerCommentUnavailableReason.HasPrPlan:
          onPlannerCommentPostError('PRプランのため、アプローチできません。')
          return
        case KartePlannerCommentUnavailableReason.ReachedToMonthlyLimit:
          onPlannerCommentPostError(
            '今月の送信数上限に達したためアプローチできません。'
          )
          return
        case KartePlannerCommentUnavailableReason.Unknown:
          onPlannerCommentPostError(
            'アプローチの送信中にエラーが発生しました。お手数ですが、時間を置いて再度送信してください。'
          )
          return
        default:
          onPlannerCommentPostError(
            'アプローチの送信中にエラーが発生しました。お手数ですが、時間を置いて再度送信してください。'
          )
          return
      }
    }

    alert.success('アプローチが完了しました。')
    closeCommentModal()
    if (
      currentUser.value?.karte?.id !== undefined &&
      karteCommentFormText.value
    ) {
      await logger.ltlPeople.event.sendKartePlannerComment(logger.buildMeta(), {
        data: {
          karteId: currentUser.value.karte.id,
          comment: karteCommentFormText.value,
          selections: karteCommentFormSelections.value,
          referrerType: '',
        },
      })
    }
  } finally {
    isLoadingPostComment.value = false
  }
}
</script>

<style lang="scss" module>
.wrapper {
  overflow: scroll;
  width: 100%;
  height: 100%;
  padding: 28px;
  background-color: $color-surface-primary;

  &__loading {
    display: flex;
    height: 100%;
    align-items: center;
    justify-content: center;
  }
}

.title {
  display: flex;
  height: 36px;
  align-items: center;
  justify-content: space-between;

  &__text {
    font-size: 20px;
    font-weight: bold;
  }

  &__close {
    height: auto;
    font-size: 36px;
  }
}

.karte {
  display: flex;
  flex-direction: column;
  gap: 24px;

  &__modal {
    border-radius: 6px;
    box-shadow: $shadow-20;
  }

  &__modal2 {
    border-radius: 12px;
  }
}

.header {
  display: flex;
  justify-content: space-between;
  padding-top: 18px;

  &__comment {
    margin-left: 32px;
  }

  &__time {
    margin-left: auto;
    color: $color-text-and-object-medium-emphasis;
  }
}

.userProfile {
  display: flex;
  flex-direction: column;

  &__box {
    &:nth-child(n + 2) {
      margin-top: 18px;
    }
  }
}

.showDiagnosis {
  font-size: 12px;
  font-weight: bold;
  margin-top: 12px;
  color: $color-text-and-object-accent-primary;
  text-decoration: underline;
}

.diagnosis {
  display: flex;
  background-color: $color-surface-secondary;
  gap: 16px;
  padding: 16px;
  border-radius: 6px;
  align-items: start;

  &__basic {
    background-color: $color-surface-primary;
    flex: 0 0 240px;
    padding: 20px 16px;
    border-radius: 6px;
  }

  &__additional {
    background-color: $color-surface-primary;
    padding: 20px 16px;
    flex: 1;
    border-radius: 6px;
  }
}

.comment {
  &__detail {
    &:nth-child(n + 2) {
      border-top: 1px solid $color-border-low-emphasis;
    }
  }

  &__loading {
    display: flex;
    min-height: 240px;
    align-items: center;
    justify-content: center;
  }
}
</style>
